"use client";

import posthogJs from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { isWithinGateImpressionCap } from "@/lib/analytics/gate-impression-cap";
import type { CheckoutFailureReason } from "@/lib/billing/checkout-failure-reason";
import type { Market } from "@/lib/market/market";
import type { SeekerProductKey } from "@/lib/mayar/products";
import type { JobApplyOutboundProps } from "./events";
import { rememberRankContext, resolveRankContext } from "./rank-context";
import { trackJobInteraction } from "./track";

/** localStorage key `trackApplyClick` stamps with `Date.now()` on every
 * outbound apply click — the real-outbound-apply-interaction signal
 * `RecentApplyClickPushPrompt` (`@/components/jobs/job-detail-client`) reads
 * instead of the old email-click-count heuristic. Exported so both sides of
 * the write/read pair share one string literal. */
export const RECENT_APPLY_CLICK_KEY = "recent-apply-outbound-click-at";

/**
 * Capture a client event AND ping the server-side `/api/telemetry/notify`
 * route so it lands in the real-time Telegram activity chat. PostHog is
 * still the source of truth — the fetch is a fire-and-forget side
 * channel. Server allowlist (`NOTIFIABLE_EVENTS`) drops unknown events,
 * so a stale build can't spam the chat.
 */
export function captureAndNotify(
	event: string,
	properties: Record<string, unknown>,
): void {
	try {
		posthogJs.capture(event, properties);
	} catch {
		/* posthog may be uninitialised */
	}
	try {
		void fetch("/api/telemetry/notify", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ event, properties }),
			keepalive: true,
		}).catch(() => undefined);
	} catch {
		/* never break the UI on a failed notify */
	}
}

type CheckoutSurface =
	| "pricing_page"
	| "upgrade_modal"
	| "feed_signup_prompt"
	| "pricing_sticky_mobile";

type CheckoutProps = {
	plan_name: SeekerProductKey;
	/**
	 * Charged amount in the currency's analytics unit, matching the
	 * `checkout_completed` webhook: IDR → rupiah (e.g. 39000), USD → cents (e.g.
	 * 900). Keeps the redirected→completed funnel comparable per currency.
	 */
	price_local?: number;
	/**
	 * "IDR" for the Indonesia/Mayar rail (the default for every caller), "USD" for
	 * the global/Buy Me a Coffee rail. The `{ currency: "IDR", ...props }` spread in
	 * each tracker lets a caller override the default — required so international
	 * checkouts aren't mislabeled as IDR in the revenue funnel.
	 */
	currency?: "IDR" | "USD";
	entry_surface?: CheckoutSurface;
	trigger_intent?: "premium_filters" | "rich_category" | "browse";
	founding_slots_remaining?: number;
	/** Price-ladder experiment bucket — lets PostHog break checkout funnels by variant. */
	price_variant?: "control" | "v1" | "v2";
	/**
	 * Audience/market tag ("indonesia" | "global") so revenue funnels can be
	 * split by market without inferring from currency. Optional — callers that
	 * have it in scope (e.g. SubscribeButton) pass it through.
	 */
	market?: Market;
	locale?: string;
};

// Stable IDs for every in-app paywall/auth gate so we can attribute
// upgrade attempts back to a specific surface. Required on every gate
// event — null/missing values made the 90d gate funnel unreadable.
export type GateSurfaceId =
	| "feed_card_locked"
	| "filter_auth_gate"
	| "apply_signup_gate"
	| "apply_login_gate"
	| "apply_upgrade_gate"
	| "apply_pro_hint"
	| "save_cap_gate"
	| "premium_filters_gate"
	| "cover_letter_gate"
	| "autofill_gate"
	| "resume_gen_gate"
	| "nudge_3rd_apply"
	| "seeker_header_upgrade"
	| "coaching_gate"
	| "match_teaser_gate";

export type GateTrigger =
	| "view_apply"
	| "view_detail"
	| "filter_locked"
	| "save_action"
	| "cover_letter_request"
	| "autofill_request"
	| "resume_limit"
	| "apply_streak"
	| "coaching_limit";

export type GateImpressionProps = {
	surface_id: GateSurfaceId;
	gate_type: "signup" | "login" | "upgrade" | "filter" | "save_cap";
	trigger?: GateTrigger;
	job_id?: string;
	blocked_feature?: string;
	blocked_value?: string;
	plan_shown?: "monthly" | "annual" | "founding";
	/** Locked cards in this feed render — `feed_card_locked` fires once per
	 * render (not once per card, see job-feed-rows.tsx) so the count carries
	 * what the per-card job_id used to. */
	locked_card_count?: number;
	/**
	 * WHICH wall this is, when `surface_id` alone is ambiguous. The apply
	 * upgrade gate fires for both a daily-apply cap and a rich-category lock
	 * (`canApply === false` covers both), so without this the two cannot be
	 * told apart downstream and a daily-cap funnel can't be isolated.
	 */
	gate_cause?: "daily_cap" | "rich_category" | "signup";
	/**
	 * `apply-paywall-relaxed` arm, on gates where that experiment applies.
	 *
	 * The two arms are stopped on DIFFERENT surfaces — control at the
	 * client-side upgrade gate, variant at the server `/out` wall — so without
	 * an arm tag on a shared surface no funnel can compare them. See
	 * src/lib/experiments/apply-paywall-relaxed.ts.
	 */
	paywall_relaxed?: boolean;
	/**
	 * Whether the emitting component had already resolved `useJobContext()`
	 * (`loading === false`) before firing. Always `true` today — the gate
	 * effect now guards on `loading` and never fires while unresolved — but
	 * kept explicit so a future regression that re-fires this from an
	 * unresolved mount is a visible `false` in the data, not a silent
	 * re-inflation of gate volume (T3, docs/plans/session-replay-ux-audit-2026-08-02.md).
	 */
	resolved?: boolean;
};

type GateCtaClickProps = GateImpressionProps & {
	cta_label?: string;
};

type GateAbandonProps = GateImpressionProps & {
	/** ms from the gate impression to the visitor leaving. */
	dwell_ms: number;
	/** How the exit was observed. */
	exit: "pagehide" | "hidden";
};

/**
 * Surfaces that got a gate CTA click, so an abandon is never recorded for a
 * gate the visitor actually converted on.
 *
 * MODULE scope, not a per-hook ref, on purpose: `JobApplyCta` mounts twice per
 * job-detail render (sidebar + mobile fixed footer, CSS decides which is
 * visible), so the instance that fires the impression is frequently NOT the
 * instance whose CTA the visitor clicks. A per-instance ref would then record
 * an abandon for a converted gate — precisely the false positive this event
 * exists to avoid.
 *
 * Keyed by surface AND job so a click on one job's gate cannot suppress the
 * abandon on the next job's.
 */
const convertedGates = new Set<string>();

function gateKey(props: GateImpressionProps): string {
	return `${props.surface_id}:${props.job_id ?? ""}`;
}

/** Test-only: module state outlives a `renderHook`, so cases must reset it. */
export function __resetConvertedGatesForTest(): void {
	convertedGates.clear();
}

// Hunt deep-coaching offers (mock interview / CV deep-dive) — every surface
// that can show the offer, so adoption is comparable across the chat, the
// Prep tab, the application tracker, and (future) the post-apply moment.
export type CoachingOfferSurface =
	| "hunt_chat"
	| "prep_panel"
	| "tracker_interview"
	| "post_apply";

export type CoachingOfferKind = "mock_interview" | "cv_deep_dive";

type CoachingOfferProps = {
	surface: CoachingOfferSurface;
	kind: CoachingOfferKind;
	locale?: string;
};

// Known AI feedback PostHog events (fired by PostFeedbackWidget and JobSummaryThumbs):
// ai_feedback_prompt_shown, ai_feedback_dismissed, ai_feedback_submitted, ai_feedback_submitted_inline
//
// The feed thumbs-up/down banner (FeedFeedbackBanner, feed_feedback_shown /
// feed_feedback_submitted) was removed 2026-08-12 (P2-12,
// telemetry-discovery-pmf-audit-2026-08-12): 2 submissions / 309 shown over
// 60d, a dead explicit-feedback instrument. Implicit signals (dwell via
// view_engagement, skip via dismiss/hunt_job_decisions) already feed the
// matcher — see src/lib/personalization/behavioral-signals.ts.

export function useAnalytics() {
	const posthog = usePostHog();

	const trackJobClick = useCallback(
		// `jobId: null` is a click-through to a job LIST (e.g. the intel CTAs in
		// src/components/intel/intel-funnel-cta.tsx link to a filtered feed, not
		// one job). PostHog still gets the event with its surface so the
		// intel->job CTR is one query; the D1 interaction row needs a real job id,
		// so it is skipped.
		(jobId: string | null, metadata: Record<string, unknown>) => {
			// Rank context (PostHog audit 2026-10-21): every click carries the
			// same six keys, null when the surface does not know them. A click
			// that names no `list_surface` is attributed to its `surface` /
			// `origin_surface`, which is the list it was rendered in.
			const rank = resolveRankContext(jobId, {
				...metadata,
				list_surface:
					metadata.list_surface ?? metadata.surface ?? metadata.origin_surface,
			});
			posthog.capture("job_card_clicked", {
				job_id: jobId,
				...metadata,
				...rank,
			});
			// Stash for the detail page / its apply CTA, which cannot otherwise
			// know which list position the visitor came from.
			if (jobId) rememberRankContext(jobId, rank);
			if (!jobId) return;
			const surface =
				typeof metadata.surface === "string" ? metadata.surface : undefined;
			const locale =
				typeof metadata.locale === "string" ? metadata.locale : undefined;
			trackJobInteraction({
				jobId,
				interactionType: "click",
				surface,
				locale,
				metadata,
			});
		},
		[posthog],
	);

	const trackApplyClick = useCallback(
		(
			jobId: string,
			payload: JobApplyOutboundProps,
			extra?: Record<string, unknown>,
		) => {
			// `surface` and `origin_surface` are two names for one thing, and only
			// one of them is complete. `origin_surface` is the canonical taxonomy
			// and is set at EVERY call site (detail, detail_mobile_footer, feed,
			// editors_picks, hunt_approval_card, job_alert_email).
			// `extra.surface` is passed by only three call sites, all inside
			// job-apply-cta.tsx, so it is ABSENT on every feed and hunt click.
			//
			// Reading `properties.surface` as "the apply surface" therefore drops
			// ~32% of applies and invents a data blindspot that does not exist —
			// which is exactly what happened on 2026-09-05, when a 67/27/5
			// detail/feed/hunt split was misread as "PostHog cannot see feed
			// applies" and nearly triggered instrumentation work to fix nothing.
			//
			// Filling `surface` from `origin_surface` when the specific value is
			// absent means the two fields can never disagree again: whichever one
			// a reader reaches for, they get a complete series. The D1 beacon
			// below deliberately keeps writing raw `origin_surface` so its stored
			// vocabulary does not shift under existing rows.
			const resolvedSurface =
				(typeof extra?.surface === "string" ? extra.surface : undefined) ??
				payload.origin_surface;
			// Explicit payload/extra values win; an apply from the detail page
			// inherits the list position stashed by the click that opened it.
			const rank = resolveRankContext(jobId, { ...payload, ...extra });
			posthog.capture("job_apply_outbound_clicked", {
				event_version: 1,
				schema_version: 1,
				occurred_at: new Date().toISOString(),
				job_id: jobId,
				...payload,
				...extra,
				...rank,
				surface: resolvedSurface,
			});
			trackJobInteraction({
				jobId,
				interactionType: "apply_outbound",
				surface: payload.origin_surface,
				locale: payload.locale,
				metadata: {
					source_platform: payload.source_platform,
					user_state: payload.user_state,
					lock_state: payload.lock_state,
				},
			});
			// Client-side marker for `RecentApplyClickPushPrompt`
			// (`job-detail-client.tsx`) — a real, verified-occurring signal
			// (614 `job_apply_outbound_clicked`/30d) the push-opt-in value-moment
			// can key on instead of a bot-prone email-click-count heuristic
			// (telemetry-gap-remediation 2026-07-21).
			try {
				window.localStorage.setItem(RECENT_APPLY_CLICK_KEY, String(Date.now()));
			} catch {
				// localStorage unavailable (private mode) — the value moment just
				// never fires for this visitor; never break the apply click itself.
			}
		},
		[posthog],
	);

	/**
	 * Returns whether the impression was actually captured, so a caller can arm
	 * follow-on telemetry (see `trackGateAbandoned`) only for gates that exist
	 * in the data. Arming it on a capped impression would make abandons exceed
	 * impressions and put the funnel above 100%.
	 */
	const trackGateImpression = useCallback(
		(props: GateImpressionProps): boolean => {
			// Per-session cap (P2-1, product-signal-audit-remediation-2026-08-04
			// §12) — see gate-impression-cap.ts for the constant and rationale.
			// Past the cap, suppress the capture only; gate RENDERING (and any
			// underlying auth/paywall requirement) is untouched — this bounds the
			// telemetry/suppression-logic load, not what a visitor can access.
			if (!isWithinGateImpressionCap()) return false;
			// A re-shown gate is a new chance to convert, so clear any earlier
			// conversion for this surface+job rather than letting one click
			// suppress every future abandon on it.
			convertedGates.delete(gateKey(props));
			posthog.capture("gate_impression", props);
			return true;
		},
		[posthog],
	);

	const trackGateCtaClick = useCallback(
		(props: GateCtaClickProps) => {
			convertedGates.add(gateKey(props));
			posthog.capture("gate_cta_clicked", props);
		},
		[posthog],
	);

	/**
	 * The visitor met a gate and left without taking its CTA.
	 *
	 * Until now this was only inferrable by subtraction (`gate_impression` minus
	 * `gate_cta_clicked`), which cannot distinguish "read the wall and refused"
	 * from "never saw it" — the two have opposite product meanings and the
	 * subtraction silently merges them. `dwell_ms` is what separates them: a
	 * 200ms exit is a bounce past the gate, a 20s exit is a considered refusal.
	 *
	 * Sent over `sendBeacon` because it fires during page teardown, where a
	 * normal XHR is cancelled and the event is simply lost — which would bias
	 * the measurement toward whoever leaves slowly.
	 */
	const trackGateAbandoned = useCallback(
		(props: GateAbandonProps) => {
			if (convertedGates.has(gateKey(props))) return;
			// One abandon per gate. `pagehide` and `visibilitychange` both fire on
			// a real mobile app-switch, and a bfcache restore can fire them again.
			convertedGates.add(gateKey(props));
			posthog.capture("gate_abandoned", props, { transport: "sendBeacon" });
		},
		[posthog],
	);

	const trackSaveToggle = useCallback(
		(jobId: string, isSaved: boolean, metadata: Record<string, unknown>) => {
			posthog.capture(isSaved ? "job_saved" : "job_unsaved", {
				job_id: jobId,
				...metadata,
			});
			const surface =
				typeof metadata.surface === "string" ? metadata.surface : undefined;
			const locale =
				typeof metadata.locale === "string" ? metadata.locale : undefined;
			trackJobInteraction({
				jobId,
				interactionType: isSaved ? "save" : "unsave",
				surface,
				locale,
				metadata,
			});
		},
		[posthog],
	);

	const trackPagination = useCallback(
		(page: number, metadata: Record<string, unknown>) => {
			posthog.capture("feed_page_paginated", {
				feed_page_number: page,
				...metadata,
			});
		},
		[posthog],
	);

	const trackPricingView = useCallback(
		(props: CheckoutProps) => {
			posthog.capture("pricing_plan_viewed", { currency: "IDR", ...props });
		},
		[posthog],
	);

	const trackPricingCtaClick = useCallback(
		(props: CheckoutProps) => {
			posthog.capture("pricing_cta_clicked", { currency: "IDR", ...props });
		},
		[posthog],
	);

	// Click-intent only — the server action (`createCheckout`) is the sole
	// `checkout_started` emitter (T1 dedupe, 2026-07-22: PR #2617 added a
	// server-side `checkout_started` alongside this one, double-counting every
	// start). Renamed rather than deleted: this fires the moment the CTA is
	// clicked, before the redirect, so it still catches intent that never
	// reaches the server (network drop, ad-blocker, hard navigation away).
	const trackCheckoutCtaClick = useCallback(
		(props: CheckoutProps) => {
			posthog.capture("checkout_cta_clicked", {
				currency: "IDR",
				product_type: "seeker_pro",
				...props,
			});
		},
		[posthog],
	);

	const trackCheckoutFailed = useCallback(
		(
			props: CheckoutProps & {
				error_code?: string;
				/** Coarse cause bucket — see `classifyCheckoutFailure`. */
				reason?: CheckoutFailureReason;
			},
		) => {
			posthog.capture("checkout_failed", {
				currency: "IDR",
				product_type: "seeker_pro",
				...props,
			});
		},
		[posthog],
	);

	const trackSubmitJobView = useCallback(
		(metadata: Record<string, unknown> = {}) => {
			posthog.capture("submit_job_form_viewed", metadata);
		},
		[posthog],
	);

	const trackSubmitJobSubmitted = useCallback(
		(metadata: Record<string, unknown> = {}) => {
			posthog.capture("submit_job_form_submitted", metadata);
		},
		[posthog],
	);

	const trackAffiliateClick = useCallback(
		(metadata: {
			platform: string;
			surface: string;
			locale?: string;
			job_id?: string | null;
			/** Shared id joining this click to its impression + later conversion. */
			click_id?: string;
			/** Copy A/B variant id applied to this offer, if it has one registered. */
			variant_id?: string;
			/** Wave 4c targeting decision: exploit | explore | fatigue_rotate |
			 * lock_exploit | fallback_global. */
			decision?: string;
			/** Wave 4b — which branch of the copy-variant bandit produced this
			 * render: control | variant_exploit | variant_explore. */
			copy_decision?: string;
			/** Wave 4c decision trace — see `decisionTraceProps`. */
			context_key?: string;
			backoff_level?: number;
			targeting_group?: string;
			p_best?: number;
			sampled_score?: number;
			rank_position?: number;
		}) => {
			// `partner` mirrors `platform` (the engine's arm/provider id — see
			// NormalizedOffer.provider) as its own top-level property so PostHog
			// insights can group/filter affiliate events by partner without a
			// `platform` alias. Not a new lookup — same value already threaded
			// through every call site's `platform` field.
			posthog.capture("affiliate_click", {
				...metadata,
				partner: metadata.platform,
			});
		},
		[posthog],
	);

	const trackAffiliateImpression = useCallback(
		(metadata: {
			platform: string;
			surface: string;
			locale?: string;
			job_id?: string | null;
			/** Shared id joining this impression to its click + later conversion. */
			click_id?: string;
			/** Geo ad market the engine targeted (indonesia | global). */
			market?: string;
			/** Why the engine's winner ranked first: measured CTR vs payout prior. */
			decision_reason?: string;
			/** Ranked eligible arms (winner first) — the auction counterfactual. */
			eligible_arms?: string[];
			/** Copy A/B variant id applied to this offer, if it has one registered. */
			variant_id?: string;
			/** Wave 4c targeting decision: exploit | explore | fatigue_rotate |
			 * lock_exploit | fallback_global. */
			decision?: string;
			/** Wave 4b — which branch of the copy-variant bandit produced this
			 * render: control | variant_exploit | variant_explore. */
			copy_decision?: string;
			/** Wave 4c decision trace — see `decisionTraceProps`. */
			context_key?: string;
			backoff_level?: number;
			targeting_group?: string;
			p_best?: number;
			sampled_score?: number;
			rank_position?: number;
			/** Placement anchor the unit rendered at (PR-5 placement experiments). */
			anchor?: string;
			/** Position index within a list surface (e.g. feed inline ad slot). */
			position_index?: number;
			/** Placement zone (e.g. "app", "web") — the telemetry-contract dimension
			 * that lets CTR be sliced by zone alongside surface. */
			zone?: string;
			/** Route family (job, jobs, blog, hunt, dashboard, email:<type>, other) —
			 * the "no blindspot" coverage dimension from the ads telemetry contract. */
			page_family?: string;
			/** Fraction of the unit visible when the impression fired (0-1). */
			viewport_ratio?: number;
			/** ms since navigation start when the unit became viewable. */
			since_nav_ms?: number;
			/** Document-space top edge of the unit (px) at fire time. */
			slot_top_px?: number;
			/** Viewport height (px) at fire time — pair with `slot_top_px`. */
			viewport_h?: number;
		}) => {
			// See trackAffiliateClick — `partner` mirrors `platform` so impressions
			// and clicks are both queryable by the same dimension.
			posthog.capture("affiliate_impression", {
				...metadata,
				partner: metadata.platform,
			});
		},
		[posthog],
	);

	const trackAdSlotImpression = useCallback(
		(
			slot: string,
			metadata: {
				locale?: string;
				ads_enabled: boolean;
				slot_type: "placeholder" | "adsense" | "adsterra";
				/** Resolved market — splits ad performance global vs indonesia. */
				market?: string;
				/** Anti-cannibalization zone — informational vs conversion. */
				zone?: string;
				/**
				 * AdSense experiment arm (`adsense` | `first_party` | `default` |
				 * `override`, see `@/lib/ads/experiment`). Compare arms by this plus
				 * `slot`; PostHog also attaches `$feature/adsense-slot-experiment`.
				 */
				arm?: string;
				/** ms since navigation start when the slot became viewable. */
				since_nav_ms?: number;
				/** Document-space top edge of the slot (px) at fire time. */
				slot_top_px?: number;
				/** Viewport height (px) at fire time — pair with `slot_top_px`. */
				viewport_h?: number;
			},
		) => {
			posthog.capture("ad_slot_impression", { slot, ...metadata });
		},
		[posthog],
	);

	const trackAdSlotClicked = useCallback(
		(
			slot: string,
			metadata: {
				locale?: string;
				market?: string;
				zone?: string;
				/** AdSense experiment arm — see `trackAdSlotImpression`. */
				arm?: string;
				/** What the click sells; "advertise" = our own paid placement. */
				intent?: string;
			},
		) => {
			posthog.capture("ad_slot_clicked", { slot, ...metadata });
		},
		[posthog],
	);

	// Google returned an unfilled AdSense unit; the slot falls back to the house
	// CTA. Once per slot per pageview (the component guards). Unfill rate =
	// ad_slot_unfilled / ad_slot_impression{slot_type=adsense}.
	const trackAdSlotUnfilled = useCallback(
		(metadata: {
			slot: string;
			locale?: string;
			market?: string;
			zone?: string;
			reason: "status" | "timeout";
		}) => {
			posthog.capture("ad_slot_unfilled", metadata);
		},
		[posthog],
	);

	const trackAdCspBlocked = useCallback(
		(metadata: { slot: string; hosts: string[]; locale?: string }) => {
			// The ad loader runs inside `/api/ads/adsterra-frame`, whose own CSP is
			// deliberately tight. Adsterra rotates the domains it fetches from, so a
			// rotation shows up here as a blocked host — and this event is the ONLY
			// way we would learn: the repo wires no `report-uri`/`report-to` anywhere,
			// so the report-only policy reports to nobody. A blocked fill used to be
			// indistinguishable from "the network had no ad", which is how the last
			// one went unnoticed.
			posthog.capture("ad_csp_blocked", {
				...metadata,
				hosts: metadata.hosts.join(","),
			});
		},
		[posthog],
	);

	const trackAdProUpsellClicked = useCallback(
		(metadata: {
			surface: string;
			locale?: string;
			provider?: string;
			market?: string;
			zone?: string;
			/** AdSense experiment arm — see `trackAdSlotImpression`. */
			arm?: string;
		}) => {
			// Fires when a free/anon viewer clicks the "go ad-free → Pro" micro-link
			// on a native affiliate card. Measures the ad-surface → /pricing funnel
			// (the house upsell), distinct from `affiliate_click` (the partner click).
			posthog.capture("ad_pro_upsell_clicked", metadata);
		},
		[posthog],
	);

	const trackAdRemoveUpsellClicked = useCallback(
		(metadata: { surface: string; locale?: string; provider?: string }) => {
			// "Remove ads — Pro →", the ONLY exit from a non-closable ad unit
			// (`adsDismissible: false`). Its own event so the annoyance guardrail
			// (G5) can read "people want the ads gone" apart from the generic
			// ad → pricing funnel that `ad_pro_upsell_clicked` still counts.
			posthog.capture("ad_remove_upsell_clicked", metadata);
		},
		[posthog],
	);

	const trackAdvertisePageViewed = useCallback(
		(metadata: { locale: string; referrer?: string }) => {
			posthog.capture("advertise_page_viewed", metadata);
		},
		[posthog],
	);

	const trackAiEmployeesPageViewed = useCallback(
		(metadata: { locale: string; referrer?: string }) => {
			posthog.capture("ai_employees_page_viewed", metadata);
		},
		[posthog],
	);

	const trackAdvertiseEmailCtaClicked = useCallback(
		(metadata: { locale: string }) => {
			posthog.capture("advertise_email_cta_clicked", metadata);
		},
		[posthog],
	);

	const trackCoachingOfferShown = useCallback(
		(metadata: CoachingOfferProps) => {
			posthog.capture("coaching_offer_shown", metadata);
		},
		[posthog],
	);

	const trackCoachingOfferClicked = useCallback(
		(metadata: CoachingOfferProps) => {
			posthog.capture("coaching_offer_clicked", metadata);
		},
		[posthog],
	);

	/**
	 * A seeker-facing empty state's single CTA was clicked — the escape-rate
	 * metric for T6 (copy-voice-overhaul-2026-08-17). `surface` identifies
	 * which empty state (e.g. "digest_no_issues", "companies_no_results") so
	 * per-surface escape rate is queryable, not just an aggregate count.
	 */
	const trackEmptyStateCtaClicked = useCallback(
		(metadata: { surface: string; locale: string }) => {
			posthog.capture("empty_state_cta_clicked", metadata);
		},
		[posthog],
	);

	// CV-first onboarding (WO-5a, `cv-first-onboarding` flag) — the first Hunt
	// session's single CV-drop/paste-LinkedIn/skip ask, gated in
	// `@/components/hunt/cv-first-onboarding-gate`. `surface` is always
	// "hunt_first_session" today (the gate's only mount point) but stays a
	// string, matching every other surface-tagged event in this file, so a
	// second mount point never needs a schema change.
	const trackCvFirstShown = useCallback(
		(metadata: { surface: string; locale: string }) => {
			posthog.capture("cv_first_shown", metadata);
		},
		[posthog],
	);

	const trackCvFirstSkipped = useCallback(
		(metadata: { surface: string; locale: string }) => {
			posthog.capture("cv_first_skipped", metadata);
		},
		[posthog],
	);

	const trackCvFirstUploaded = useCallback(
		(metadata: { surface: string; locale: string }) => {
			posthog.capture("cv_first_uploaded", metadata);
		},
		[posthog],
	);

	return {
		trackJobClick,
		trackApplyClick,
		trackGateImpression,
		trackGateCtaClick,
		trackGateAbandoned,
		trackSaveToggle,
		trackPagination,
		trackPricingView,
		trackPricingCtaClick,
		trackCheckoutCtaClick,
		trackCheckoutFailed,
		trackSubmitJobView,
		trackSubmitJobSubmitted,
		trackAffiliateClick,
		trackAffiliateImpression,
		trackAdSlotImpression,
		trackAdSlotClicked,
		trackAdCspBlocked,
		trackAdSlotUnfilled,
		trackAdProUpsellClicked,
		trackAdRemoveUpsellClicked,
		trackAdvertisePageViewed,
		trackAiEmployeesPageViewed,
		trackAdvertiseEmailCtaClicked,
		trackCoachingOfferShown,
		trackCoachingOfferClicked,
		trackEmptyStateCtaClicked,
		trackCvFirstShown,
		trackCvFirstSkipped,
		trackCvFirstUploaded,
		posthog,
	};
}
